import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"
import { showHubTeamToast } from "../utils/hub_team_toast"

export default class extends Controller {
  static targets = ["modal", "fileInput", "submitButton", "errorAlert", "errorMessage"]
  static values = { url: String, maxFileSize: Number }

  connect() {
    this.handleModalHidden = this.handleModalHidden.bind(this)

    if (this.hasModalTarget) {
      this.modalTarget.addEventListener("hidden.bs.modal", this.handleModalHidden)
    }

    this.validate()
  }

  disconnect() {
    if (this.hasModalTarget) {
      this.modalTarget.removeEventListener("hidden.bs.modal", this.handleModalHidden)
    }
  }

  open(event) {
    event.preventDefault()
    this.resetForm()
    Modal.getOrCreateInstance(this.modalTarget).show()
    this.validate()
  }

  validate() {
    if (!this.hasSubmitButtonTarget) return

    const saving = this.submitButtonTarget.dataset.saving === "true"
    const file = this.selectedFile()
    const fileSelected = file !== null

    if (file && this.fileTooLarge(file)) {
      this.showError(this.fileTooLargeMessage())
      this.submitButtonTarget.disabled = true
      return
    }

    this.clearError()
    this.submitButtonTarget.disabled = saving || !fileSelected
  }

  async import(event) {
    event.preventDefault()

    const file = this.selectedFile()
    if (!file || !this.hasUrlValue) return

    if (!file.name.toLowerCase().endsWith(".csv")) {
      this.showError("Only CSV files are supported.")
      return
    }

    if (this.fileTooLarge(file)) {
      this.showError(this.fileTooLargeMessage())
      return
    }

    this.setSaving(true)
    this.clearError()

    const formData = new FormData()
    formData.append("file", file)

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        body: formData,
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
          "Accept": "application/json"
        },
        credentials: "same-origin"
      })

      const data = await response.json()

      if (response.ok && data.success) {
        Modal.getOrCreateInstance(this.modalTarget).hide()
        this.showToast(data.message || "Import started. You will be notified when it completes.")
        this.refreshNotificationBell()
      } else {
        const message = data?.errors?.join(", ") || `HTTP ${response.status}`
        this.showError(message)
      }
    } catch (error) {
      console.error("Error uploading team import CSV:", error)
      this.showError("An error occurred. Please try again.")
    } finally {
      this.setSaving(false)
      this.validate()
    }
  }

  selectedFile() {
    if (!this.hasFileInputTarget) return null

    const fileUpload = this.fileInputTarget.closest("[data-controller~='file-upload']")
    const input = fileUpload?.querySelector('input[type="file"]') || this.fileInputTarget

    return input?.files?.[0] || null
  }

  fileTooLarge(file) {
    return this.hasMaxFileSizeValue && file.size > this.maxFileSizeValue
  }

  fileTooLargeMessage() {
    const limit = this.hasMaxFileSizeValue
      ? this.formatFileSize(this.maxFileSizeValue)
      : "1 MB"

    return `CSV file is too large (maximum is ${limit}).`
  }

  formatFileSize(bytes) {
    if (bytes >= 1_048_576) {
      const megabytes = bytes / 1_048_576
      return Number.isInteger(megabytes) ? `${megabytes} MB` : `${megabytes.toFixed(1)} MB`
    }

    if (bytes >= 1_024) {
      const kilobytes = bytes / 1_024
      return Number.isInteger(kilobytes) ? `${kilobytes} KB` : `${kilobytes.toFixed(1)} KB`
    }

    return `${bytes} bytes`
  }

  resetForm() {
    const fileUpload = this.fileInputTarget?.closest("[data-controller~='file-upload']")
    const input = fileUpload?.querySelector('input[type="file"]')

    if (input) {
      input.value = ""
      const controller = this.application.getControllerForElementAndIdentifier(fileUpload, "file-upload")
      if (controller?.accumulatedFiles) {
        controller.accumulatedFiles = new DataTransfer()
      }
      fileUpload?.querySelector("[data-file-upload-target='list']")?.replaceChildren()
    }

    this.clearError()
    this.validate()
  }

  handleModalHidden() {
    this.resetForm()
  }

  setSaving(saving) {
    if (!this.hasSubmitButtonTarget) return

    this.submitButtonTarget.dataset.saving = saving ? "true" : "false"
    this.submitButtonTarget.textContent = saving ? "Uploading..." : "Upload and import"
    this.validate()
  }

  showError(message) {
    if (!this.hasErrorAlertTarget || !this.hasErrorMessageTarget) return

    this.errorMessageTarget.textContent = message
    this.errorAlertTarget.classList.remove("d-none")
  }

  clearError() {
    if (!this.hasErrorAlertTarget || !this.hasErrorMessageTarget) return

    this.errorMessageTarget.textContent = ""
    this.errorAlertTarget.classList.add("d-none")
  }

  showToast(message) {
    showHubTeamToast(message)
  }

  refreshNotificationBell() {
    const bell = document.querySelector("[data-controller~='notification-bell']")
    if (!bell) return

    const controller = this.application.getControllerForElementAndIdentifier(bell, "notification-bell")
    controller?.refreshUnreadCount()
  }
}
